import { FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useCourseChapters from '@hooks/useCouseChapters'
import { useGetCourseByIdQuery } from '@services/courses'
import { PER_PAGE } from '@const/pagination'
import { createInitFilter } from '@utils/filters'
import List, { ListItemProps } from '@templates/List'
import BackButton from '@organisms/BackButton'
import Onboarding from '@templates/Onboarding'
import OnboardingCodes from '@enums/OnboardingCodes'
import { Routes } from '@enums/Routes'
import useMe from '@hooks/useMe'
import { CourseChapter } from '@models/courseChapter'
import { CourseThemeListItemServerResponse } from '@models/courseTheme'
import Chapter from './Chapter'
import FinalTestChapter from './FinalTestChapter'
import { ButtonWrapper, StyledTitle, Wrapper } from './styled'
import { ONBOARDING_STEPS, ONBOARDING_TITLE } from './onboarding'

type ChapterListItem = CourseChapter & {
  type: 'chapter'
  number: number
  courseId: string
  educationId: string | number
  isClientCourse?: boolean
}

type FinalTestListItem = {
  id: number
  type: 'finalTest'
  number: number
  finalTest: CourseThemeListItemServerResponse
  courseId: string
  educationId: string | number
}

type CourseListItem = ChapterListItem | FinalTestListItem

const CourseListItem: FC<ListItemProps<CourseListItem>> = (props) => {
  const { item } = props

  if (item.type === 'finalTest') {
    return (
      <FinalTestChapter
        finalTest={item.finalTest}
        number={item.number}
        courseId={item.courseId}
        educationId={item.educationId}
      />
    )
  }

  return <Chapter {...{ ...props, item }} />
}

const CourseComponent: FC = () => {
  const { isAdmin } = useMe()
  const { courseId, educationId } = useParams()
  const navigate = useNavigate()
  const { data: course, error } = useGetCourseByIdQuery(courseId || '', {
    skip: !courseId,
  })
  const { title, finalTest } = course || {}
  // Бэкенд не отдаёт isClientCourse: группы на свойстве стоят, но геттер называется
  // isClientCourse(), и сериализатор не может прочитать одноимённое свойство. Признак
  // клиентского курса — наличие владельца: бэкенд выставляет isClientCourse ровно
  // вместе с ним, у общих курсов владельца нет. Так же это определяет страница темы.
  const isClientCourse = Boolean(course?.isClientCourse || course?.ownerCompany)
  const {
    data: chapters,
    page,
    setPage,
    isLoading,
  } = useCourseChapters(
    courseId ? createInitFilter('course', courseId) : undefined
  )
  const { items: chaptersItems = [], length = 0 } = chapters || {}
  const items =
    (error as { status: number })?.status === 404 ? [] : chaptersItems
  const totalLength = length + (finalTest ? 1 : 0)
  const finalTestNumber = length + 1
  const firstPageItemNumber = (page - 1) * PER_PAGE + 1
  const lastPageItemNumber = page * PER_PAGE
  const showFinalTest = Boolean(
    finalTest &&
      courseId &&
      finalTestNumber >= firstPageItemNumber &&
      finalTestNumber <= lastPageItemNumber
  )

  const handleBack = () => {
    navigate(isAdmin ? Routes.COURSES : Routes.STUDY)
  }

  return (
    <>
      <Onboarding
        firstStepTitle={ONBOARDING_TITLE}
        steps={ONBOARDING_STEPS}
        code={OnboardingCodes.my_education_course}
      />
      <ButtonWrapper>
        <BackButton onClick={handleBack} />
      </ButtonWrapper>
      <Wrapper>
        <StyledTitle>{title}</StyledTitle>
      </Wrapper>
      <List
        isLoading={isLoading}
        list={[
          ...items.map((item, index) => ({
            ...item,
            type: 'chapter' as const,
            number: index + 1 + PER_PAGE * (page - 1),
            courseId: courseId || '',
            educationId: educationId || 0,
            isClientCourse,
          })),
          ...(showFinalTest && finalTest && courseId
            ? [
                {
                  id: -finalTest.id,
                  type: 'finalTest' as const,
                  number: finalTestNumber,
                  finalTest,
                  courseId,
                  educationId: educationId || 0,
                },
              ]
            : []),
        ]}
        setPage={setPage}
        length={totalLength}
        page={page}
        ItemComponent={CourseListItem}
      />
    </>
  )
}

export default CourseComponent
